import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import NavDrawer from './NavDrawer';

const C = {
  gold: '#f0c040',
  goldDim: '#c9a227',
  cyan: '#00d4ff',
  ink: '#e8ecf5',
  inkSoft: '#7a9ab8',
  inkFaint: '#4a5a7a',
  uiBg: '#04060f',
  cardBg: '#0a1020',
  border: '#1a2540',
  rowBg: 'rgba(8,12,26,0.55)',
};

const TOOLS = [
  {
    title: 'SET SUMMARIES',
    desc: 'Create, edit, publish, and delete the public Set Summary pages at /resources/sets/<id>. No code deploys needed.',
    href: '/admin/set-summaries',
    accent: C.gold,
  },
  {
    title: 'CHECKLISTS',
    desc: 'Add and edit the underlying checklist data for each set — sub-sets, cards, parallels.',
    href: '/admin/checklists',
    accent: C.cyan,
  },
  {
    title: 'TOPPS BULK EDITOR',
    desc: 'Bulk-import or manage Topps-specific checklist data.',
    href: '/admin/checklists/topps',
    accent: '#b060ff',
  },
  {
    title: 'DATA DASHBOARD',
    desc: 'The most actionable data about the site — needs-attention triage, KPIs, marketplace funnel, growth, trust, and collections. Live from one query.',
    href: '/admin/dashboard',
    accent: '#00e87a',
  },
  {
    title: 'SET INGESTION',
    desc: 'Upload a Topps PDF or CSV. AI drafts a checklist with cards, parallels, and summary copy. You review and publish.',
    href: '/admin/set-ingestion',
    accent: '#ff5c7a',
  },
];

function navigateTo(href) {
  window.history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export default function AdminHome({ user }) {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single();
      if (cancelled) return;
      if (error || !profile?.is_admin) {
        navigateTo('/');
        return;
      }
      setIsAdmin(true);
      setLoading(false);
    }
    check();
    return () => { cancelled = true; };
  }, [user.id]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: C.uiBg, fontFamily: "'Rajdhani', sans-serif", color: C.ink }}>
        <NavDrawer user={user} />
        <div style={{ textAlign: 'center', paddingTop: '120px', fontFamily: "'Orbitron', monospace", fontSize: '11px', color: C.cyan, letterSpacing: '4px' }}>
          // CHECKING ADMIN ACCESS
        </div>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div style={{ minHeight: '100vh', background: C.uiBg, fontFamily: "'Rajdhani', sans-serif", color: C.ink, WebkitFontSmoothing: 'antialiased' }}>
      <NavDrawer user={user} />
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 20px 80px' }}>

        <div style={{ marginBottom: '32px', paddingRight: '56px' }}>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '4px', color: C.goldDim, marginBottom: '8px' }}>
            COLLECTION KEEPER · ADMIN
          </div>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '24px', fontWeight: 800, color: '#fff', letterSpacing: '1.5px', marginBottom: '6px' }}>
            ADMIN TOOLS
          </div>
          <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: C.inkFaint, letterSpacing: '2px' }}>
            // INTERNAL TOOLS FOR MANAGING COLLECTIONKEEPER //
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
          {TOOLS.map((t) => (
            <a
              key={t.href}
              href={t.href}
              onClick={(e) => { e.preventDefault(); navigateTo(t.href); }}
              style={{
                display: 'block',
                background: C.rowBg,
                border: `1px solid ${C.border}`,
                borderLeft: `3px solid ${t.accent}`,
                borderRadius: '4px',
                padding: '20px 20px 22px',
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(8,12,26,0.85)'; e.currentTarget.style.borderColor = t.accent; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = C.rowBg; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.borderLeftColor = t.accent; }}
            >
              <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', color: t.accent, marginBottom: '8px' }}>
                {t.title}
              </div>
              <div style={{ fontSize: '13px', color: C.inkSoft, lineHeight: 1.5, marginBottom: '12px' }}>
                {t.desc}
              </div>
              <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: C.inkFaint, letterSpacing: '1px' }}>
                {t.href} →
              </div>
            </a>
          ))}
        </div>

        <div style={{ marginTop: '32px', fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: C.inkFaint, letterSpacing: '1px' }}>
          // {TOOLS.length} tools available · admin-only access
        </div>

      </div>
    </div>
  );
}
